import React, { useCallback, useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import type {
  AlertTypes,
  BadgeTheme,
  ProductColour,
  ProductScoreGaugeProps,
  ProsConsProps,
} from '@which/seatbelt'
import { ProductCard as Card } from '@which/seatbelt'
import { dynamicDatalayerPush, dynamicGa4DataLayerPush } from '@which/shared'

import {
  useAddSavedItemMutation,
  useRemoveSavedItemMutation,
} from '../../../../../generated/frontend'
import { focusAboveCompareTray } from '../../../../../shared/components/ReviewsCompareTray/index'
import { SavedRequestType } from '../../../../../shared/types'
import type { Comparison } from '../../../../../shared/types/ReviewsCompareTray'
import { getHost } from '../../../../../shared/utils/get-host'
import {
  ADD_TO_SAVED,
  REMOVE_FROM_SAVED,
  SAVE_ERROR,
} from '../../../shared/components/ToastNotification/constants'
import { addHandlerToRunningCostTooltip } from '../../../utils/add-handler-to-running-cost-tooltip'
import { addHandlerToTestScoreTooltip } from '../../../utils/add-handler-to-testScore-tooltip'
import { getComparisonLimitReached, getIsSelectedForComparison } from '../../../utils/comparison'
import { mapAnnualRunningCost } from '../../../utils/map-annual-running-cost'
import { ReviewsTemplateContext } from '../../ReviewsTemplateContext'
import type { ProductCard as ProductCardType, Variant } from './Products'

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  taxonomySlug,
  index,
  compareTrayItems,
  addCompareProduct,
  removeCompareProduct,
  savedProducts,
  savedCallback,
  loginStatus,
}) => {
  const { state } = useContext(ReviewsTemplateContext)
  const {
    image = { alt: '', src: '' },
    manufacturer = '',
    model = '',
    badges,
    tags,
    price = '',
    slug = '',
    retailers,
    alertType,
    features,
    productScoreGauge,
    businessKey = '',
    annualRunningCost,
    category,
    variantProducts,
    variantPriceRange,
  } = product

  const defaultVariant =
    variantProducts?.find(
      (variantProduct: Variant) => variantProduct.businessKey === businessKey
    ) ?? null

  const hasVariants = variantProducts?.length > 1
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(defaultVariant)
  const isSelectedForComparison = getIsSelectedForComparison({
    compareTrayItems,
    slug: selectedVariant?.slug || slug,
  })
  const productSlug = `${taxonomySlug}/${selectedVariant?.slug || slug}`
  const productUrl = `${getHost()}/reviews/${productSlug}`
  const history = useHistory()
  const allVariantValues: string[][] | null = hasVariants
    ? variantProducts.map((variantProduct: Variant) =>
        variantProduct.variantValues.map((v) => v.value)
      )
    : null
  const variantMinPrice = variantPriceRange?.min?.formatted
  const variantMaxPrice = variantPriceRange?.max?.formatted
  const formattedPrice =
    variantMinPrice !== variantMaxPrice && hasVariants
      ? `${variantMinPrice} - ${variantMaxPrice}`
      : price

  let productIsSaved = savedProducts?.includes(selectedVariant?.businessKey || businessKey)

  const handleVariantChange = (variant: ProductColour[]) => {
    const newlySelectedVariant =
      variantProducts?.find(
        (variantProduct: Variant) =>
          JSON.stringify(variant) ===
          JSON.stringify(variantProduct.variantValues.map((v) => v.value))
      ) ?? null

    productIsSaved = savedProducts?.includes(newlySelectedVariant?.businessKey || '')

    dynamicGa4DataLayerPush({
      event: 'click_button',
      item_text: variant.join('|'),
      item_parent_text: `${manufacturer}|${newlySelectedVariant?.model}`,
      item_group: 'product card',
      item_spec: 'product variant',
      action_group: 'switch color',
      utagid: 'RS2666DP01',
    })

    setSelectedVariant(newlySelectedVariant)
  }

  const tooltipOpenHandler = () => {
    dynamicDatalayerPush({
      event: 'tEvent',
      eventAction: 'Tooltip',
      eventCategory: 'Product Listing',
      eventInteractive: false,
      eventLabel: `${category?.toLowerCase()} - Product Card - Test score`,
      eventValue: 0,
    })
  }

  const tooltipCloseHandler = () => {
    dynamicGa4DataLayerPush({
      event: 'click_tooltip',
      utagid: 'RS2885DP01',
      item_text: 'close',
      item_parent_text: 'test score',
      item_group: 'product card',
    })
  }

  const cardCallback = (e: React.ChangeEvent<HTMLInputElement> | React.SyntheticEvent) => {
    const {
      target: { ariaLabel, innerText, nodeName },
    } = e as React.ChangeEvent<HTMLInputElement>
    const checkboxClick = innerText?.trim() === 'Compare' || nodeName === 'INPUT'
    const saveButtonClick =
      nodeName === 'svg' ||
      nodeName === 'path' ||
      ariaLabel?.includes('save product') ||
      ariaLabel?.includes('from saved products')

    if (!checkboxClick && !saveButtonClick) {
      dynamicGa4DataLayerPush({
        event: 'click_card',
        item_text: `${manufacturer} | ${model}`,
        item_parent_text: 'product listing page',
        item_group: 'product card',
        item_index: index + 1,
        item_spec: hasVariants ? 'color variants' : undefined,
        item_url: productUrl,
        utagid: 'RS2821DP01',
      })
      history.push(`/reviews/${productSlug}`)
    }
  }

  const runningCostTooltipOpenHandler = () => {
    dynamicGa4DataLayerPush({
      item_text: 'annual running cost',
      item_parent_text: 'product card',
      event: 'clickTooltip',
    })
  }

  const runningCostTooltipCloseHandler = () => {
    dynamicGa4DataLayerPush({
      event: 'click_tooltip',
      utagid: 'RS2885DP02',
      item_text: 'close',
      item_parent_text: 'annual running cost',
      item_group: 'product card',
    })
  }

  /* ===== START Save ===== */
  const [removeSavedItem, { loading: isRemovingSavedProduct }] = useRemoveSavedItemMutation({
    variables: {
      itemId: selectedVariant?.businessKey ?? businessKey,
    },
  })
  const [addSavedItem, { loading: isAddingSavedProduct }] = useAddSavedItemMutation({
    variables: {
      item: {
        itemId: selectedVariant?.businessKey ?? businessKey,
        itemCategory: taxonomySlug,
        itemType: 'product',
      },
    },
  })

  const handleSavedResponse = useCallback(
    (success: boolean, requestType: SavedRequestType) => {
      const isAddition = requestType === SavedRequestType.ADD
      if (success) {
        if (isAddition) {
          state.setToastContext(ADD_TO_SAVED)
        } else {
          const undoRemove = () => {
            addSavedItem()
              .then((response) => {
                if (response.data?.addSavedItem) {
                  handleSavedResponse(response.data.addSavedItem.success, SavedRequestType.ADD)
                }
              })
              .catch(() => {
                handleSavedResponse(false, SavedRequestType.ADD)
              })
            savedCallback(selectedVariant?.businessKey ?? businessKey)
          }
          state.setToastContext({
            ...REMOVE_FROM_SAVED,
            linkText: 'Undo',
            onClick: undoRemove,
          })
        }
      } else {
        state.setToastContext(SAVE_ERROR)
        savedCallback(selectedVariant?.businessKey ?? businessKey)
      }
    },
    [state, addSavedItem, businessKey, savedCallback, selectedVariant?.businessKey]
  )

  /*
    Note: Seatbelt <SavedButton /> only renders if saveOnClick function exists
    so this function is left undefined if the feature flag is off
    */
  const saveOnClick = () => {
    if (isRemovingSavedProduct || isAddingSavedProduct) {
      return
    }

    dynamicGa4DataLayerPush({
      event: 'click_save',
      utagid: 'WHC352DP02',
      item_group: 'saved items',
      item_product_id: selectedVariant?.businessKey ?? businessKey,
      item_spec: 'plp product card',
      item_text: `${manufacturer} ${model}`,
      action_group: `${productIsSaved ? 'remove from' : 'add to'} save`,
    })

    savedCallback(selectedVariant?.businessKey ?? businessKey)

    if (loginStatus && !loginStatus.includes('NO_ACCESS')) {
      productIsSaved
        ? removeSavedItem()
            .then((response) => {
              if (response.data?.removeSavedItem) {
                handleSavedResponse(response.data.removeSavedItem.success, SavedRequestType.REMOVE)
              }
            })
            .catch(() => {
              handleSavedResponse(false, SavedRequestType.REMOVE)
            })
        : addSavedItem()
            .then((response) => {
              if (response.data?.addSavedItem) {
                handleSavedResponse(response.data.addSavedItem.success, SavedRequestType.ADD)
              }
            })
            .catch(() => {
              handleSavedResponse(false, SavedRequestType.ADD)
            })
    }
  }
  /* ===== END Save ===== */

  return (
    <Card
      taxonomySlug={taxonomySlug}
      manufacturer={manufacturer}
      model={selectedVariant?.model || model}
      image={selectedVariant?.image || image}
      badges={(selectedVariant?.badges as BadgeTheme[]) || (badges as BadgeTheme[])}
      tags={selectedVariant?.tags || tags}
      price={formattedPrice}
      slug={selectedVariant?.slug || slug}
      retailers={selectedVariant?.hasRetailers ?? retailers}
      alertType={alertType as AlertTypes}
      prosConsList={{
        pros: (features[0].list ? features[0] : null) as ProsConsProps,
        cons: (features[1].list ? features[1] : null) as ProsConsProps,
      }}
      comparisonLimitReached={getComparisonLimitReached({ compareTrayItems })}
      isSelectedForComparison={isSelectedForComparison}
      handleAddCompare={addCompareProduct}
      handleRemoveCompare={removeCompareProduct}
      handleFocus={focusAboveCompareTray}
      productScoreGauge={addHandlerToTestScoreTooltip(
        productScoreGauge as ProductScoreGaugeProps,
        tooltipOpenHandler,
        tooltipCloseHandler
      )}
      productUrl={productUrl}
      cardCallback={cardCallback}
      businessKey={selectedVariant?.businessKey ?? businessKey}
      annualRunningCost={
        annualRunningCost &&
        addHandlerToRunningCostTooltip(
          mapAnnualRunningCost({ annualRunningCost }),
          runningCostTooltipOpenHandler,
          runningCostTooltipCloseHandler
        )
      }
      viewRetailersCallback={cardCallback}
      variants={allVariantValues as ProductColour[][]}
      selectedVariant={selectedVariant?.variantValues.map((v) => v.value) as ProductColour[]}
      handleVariantChange={handleVariantChange}
      saved={productIsSaved}
      saveOnClick={saveOnClick}
    />
  )
}

////// IMPLEMENTATION ///////

export interface ProductCardProps {
  product: ProductCardType
  taxonomySlug: string
  index: number
  compareTrayItems?: Comparison[]
  addCompareProduct: ({ manufacturer, model, businessKey, slug }: Comparison) => void
  removeCompareProduct: ({ manufacturer, model, businessKey }) => void
  savedProducts: string[]
  savedCallback: (productId: string) => void
  loginStatus: string
}
